import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { router } from 'Router';
import { RouterProvider } from 'react-router';
import { ConfigProvider } from 'antd';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import { NotificationProvider } from 'context/NotificationProvider';
import { ReactQueryProvider } from 'context/ReactQueryProvider';
import { PantryColorSchemeSync } from 'hooks/useSyncPantryColorScheme';

import { dashboardTheme } from 'config/cuboh.theme';

import { registerChunkLoadRecovery } from 'helpers/chunkLoadRecovery';
import { initSentry } from 'helpers/loggers';
import { initializeMParticle } from 'helpers/mparticle';

import { theme } from 'styles/theme.config';
import { resolver } from 'styles/theme.variables';

import './i18n';
import './App.css';
import './styles/tailwind.css';

const DATA_DOG_ENV_ALLOW_LIST = ['prod', 'stg'];

initSentry(import.meta.env.VITE_APP_VERSION);
registerChunkLoadRecovery(() => {
  const next = router.state.navigation.location;

  return next && `${next.pathname}${next.search}`;
});

(async () => {
  const canLogDD = DATA_DOG_ENV_ALLOW_LIST.includes(import.meta.env.VITE_DEPLOY_ENV);
  if (canLogDD) {
    datadogRum.init({
      applicationId: import.meta.env.VITE_DATA_DOG_APPLICATION_ID,
      clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'one-dashboard',
      version: import.meta.env.VITE_APP_VERSION,
      sampleRate: 100,
      sessionReplaySampleRate: 100,
      env: import.meta.env.VITE_DEPLOY_ENV,
      trackUserInteractions: true,
      trackLongTasks: false,
      trackResources: false,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: event => {
        // filter out only Cloudflare CSP report violations [CN-40557]
        if (
          event.type === 'error' &&
          event.error?.source === 'report' &&
          event.error?.stack?.includes('csp-reporting.cloudflare.com/cdn-cgi/scrip')
        ) {
          return false;
        }
        return true;
      },
    });
  }

  const heapScript = document.createElement('script');
  heapScript.type = 'text/javascript';
  heapScript.async = true;
  heapScript.innerHTML = `
    window.heapReadyCb = window.heapReadyCb || [], window.heap = window.heap || [], heap.load = function(e,t) {
      window.heap.envId = e, window.heap.clientConfig = t = t || {}, window.heap.clientConfig.shouldFetchServerConfig = !1;
      var a = document.createElement("script");
      a.type = "text/javascript", a.async = !0, a.src = "https://cdn.us.heap-api.com/config/" + e + "/heap_config.js";
      var r = document.getElementsByTagName("script")[0];
      r.parentNode.insertBefore(a,r);
      var n = ["init", "startTracking", "stopTracking", "track", "resetIdentity", "identify", "getSessionId", "getUserId", "getIdentity", "addUserProperties", "addEventProperties", "removeEventProperty", "clearEventProperties", "addAccountProperties", "addAdapter", "addTransformer", "addTransformerFn", "onReady", "addPageviewProperties", "removePageviewProperty", "clearPageviewProperties", "trackPageview"],
      i = function(e) {
        return function() {
          var t = Array.prototype.slice.call(arguments, 0);
          window.heapReadyCb.push({ name: e, fn: function() { heap[e] && heap[e].apply(heap, t) } })
        }
      };
      for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p])
    };
    heap.load('${import.meta.env.VITE_HEAP_API_ID}', { compressCookies: true });
  `;
  document.head.appendChild(heapScript);

  initializeMParticle();

  const root = ReactDOM.createRoot(document.getElementById('root'));

  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
  });

  root.render(
    <StrictMode>
      <LDProvider>
        <HelmetProvider>
          <Helmet titleTemplate="%s | ChowNow Dashboard" />
          <ReactQueryProvider>
            <MantineProvider theme={theme} cssVariablesResolver={resolver}>
              <PantryColorSchemeSync />
              <Notifications
                position="top-center"
                limit={2}
                autoClose={2000}
                transitionDuration={300}
                containerWidth="600px"
              />
              <DatesProvider>
                <ConfigProvider prefixCls="chownow" theme={dashboardTheme}>
                  <NotificationProvider>
                    <RouterProvider router={router} />
                  </NotificationProvider>
                </ConfigProvider>
              </DatesProvider>
            </MantineProvider>
          </ReactQueryProvider>
        </HelmetProvider>
      </LDProvider>
    </StrictMode>,
  );
})();
